"use client";
import React, { useState, useEffect } from "react";
import { useDesignation } from "../../context/DesignationContext";
import { useDepartments } from "../../context/DepartmentContext";
import { Edit, Trash2, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function Designations() {
  const {
    designations,
    loading,
    createDesignation,
    updateDesignation,
    deleteDesignation,
  } = useDesignation();

  const { departments } = useDepartments();

  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedDesignation, setSelectedDesignation] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [page, setPage] = useState(1);
  const { canDo } = useAuth();
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [expandedDesc, setExpandedDesc] = useState(null);

const [itemsPerPage, setItemsPerPage] = useState(5);

  // ✅ get logged-in user from localStorage
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user?.id || null;

  const filteredDesignations = designations.filter((d) => {
    const matchesSearch =
      d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(d.id).includes(searchQuery);
    const matchesStatus = filterStatus === "All" || d.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredDesignations.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const paginatedDesignations = filteredDesignations.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  useEffect(() => {
  setPage(1);
}, [searchQuery, filterStatus]);

  // ✅ Add new designation
  const handleAdd = async (formData) => {
    if (!userId) return alert("User not logged in!");

    const payload = {
      ...formData,
      create_audit_id: userId,
    };

    const res = await createDesignation(payload);
    if (res.success) setShowModal(false);
    else alert(res.message);
  };

  // ✅ Edit existing designation
  const handleEdit = async (formData) => {
    if (!userId) return alert("User not logged in!");

    const payload = {
      ...formData,
      update_audit_id: userId,
    };

    const res = await updateDesignation(selectedDesignation.id, payload);
    if (res.success) setEditModal(false);
    else alert(res.message);
  };

  // ✅ Delete designation
  const handleDelete = async () => {
    if (!userId) return alert("User not logged in!");

    const res = await deleteDesignation(selectedDesignation.id, userId);
    if (res.success) setDeleteModal(false);
    else alert(res.message);
  };

 useEffect(() => {
  const checkScreen = () => {
    setIsSmallScreen(window.innerWidth <= 380);
  };

  checkScreen(); // ✅ run immediately
  window.addEventListener("resize", checkScreen);

  return () => window.removeEventListener("resize", checkScreen);
}, []);

  if (loading) return <p className="text-center mt-6">Loading...</p>;

  

  return (
    <div className="bg-sky-50 px-9 py-6 max-sm:px-4">
     <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">Designations</h2>

      {/* 🔍 Search + Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-5 gap-3 bg-white px-3 py-3 rounded-xl shadow-sm">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
          <div className="flex items-center bg-white border border-gray-300 rounded-lg px-3 py-1.5 w-full sm:w-80 focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
            <input
              type="text"
              placeholder="Search by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="outline-none text-sm w-full bg-transparent"
            />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="text-sm outline-none bg-transparent border-l border-gray-300 pl-2 ml-2"
            >
              <option value="All">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {(searchQuery || filterStatus !== "All") && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                setFilterStatus("All");
              }}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 transition whitespace-nowrap shrink-0 w-full sm:w-auto"
            >
              <X size={14} />
              Clear Filters
            </button>
          )}
        </div>

        {canDo("Designations", "Add") && (
        <button
          onClick={() => setShowModal(true)}
          className=" text-white px-4 py-2 rounded-lg text-sm font-medium transition w-full sm:w-auto"
      style={{ 
  background: "linear-gradient(135deg, #2563eb, #d12465)"
}}  >
          + Create New Designation
        </button>
        )}
      </div>

      {/* 📋 Table */}
     <div className="bg-white rounded-2xl shadow-sm p-6 overflow-x-auto"> 
        {paginatedDesignations.length === 0 ? (
          <p className="text-center text-gray-500 text-sm py-6">
            No designations found.
          </p>
        ) : (
          // <table className="min-w-full border-collapse table">
            <table className="min-w-[900px] w-full border-collapse table-fixed">
            <thead>
              <tr className="bg-sky-100 text-gray-700 text-sm">
                <th className="py-3 px-4 text-left font-semibold w-[8%]">ID</th>
<th className="py-3 px-4 text-left font-semibold w-[18%]">Name</th>
<th className="py-3 px-4 text-left font-semibold w-[18%]">Department</th>
<th className="py-3 px-4 text-left font-semibold w-[40%]">Description</th>
<th className="py-3 px-2 text-left font-semibold w-[10%]">Status</th>
                {(canDo("Designations", "Edit") || canDo("Designations", "Delete")) && (
                  <th className="py-3 px-1 text-center font-semibold w-[6%]">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedDesignations.map((d, i) => (
                <tr
                  key={d.id}
                  className={`border-b text-sm ${
                    i % 2 === 1 ? "bg-[#f7f6fe]" : "bg-white"
                  }`}
                >
                  <td className="py-3 px-4 w-[8%] text-indigo-600">
  {i + 1}
</td>
                  <td className="py-3 px-4 w-[18%]">
  {d.name}
</td>
                  <td className="py-3 px-4 w-[18%]">
  {d.dep_name || "-"}
</td>
                 <td className="py-3 px-4">
  <p className="text-gray-700 break-words leading-relaxed">
    {expandedDesc === d.id
      ? d.description
      : d.description?.length > 80
        ? d.description.slice(0, 80) + "..."
        : d.description}
  </p>

  {d.description?.length > 80 && (
    <button
      className="text-indigo-600 text-xs mt-1 hover:underline"
      onClick={() =>
        setExpandedDesc(expandedDesc === d.id ? null : d.id)
      }
    >
      {expandedDesc === d.id ? "Show less" : "Read more"}
    </button>
  )}
</td>
                  <td className="py-3 px-4">
                    <span
                      className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        d.status === "active"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {d.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
  <div className="flex justify-center items-center gap-3">
    
    {canDo("Designations", "Edit") && (
      <button
        onClick={() => {
          setSelectedDesignation(d);
          setEditModal(true);
        }}
        className="text-gray-600 hover:text-indigo-600 transition"
      >
        <Edit size={16} />
      </button>
    )}

    {canDo("Designations", "Delete") && (
      <button
        onClick={() => {
          setSelectedDesignation(d);
          setDeleteModal(true);
        }}
        className="text-gray-600 hover:text-red-600 transition"
      >
        <Trash2 size={16} />
      </button>
    )}

  </div>
</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="flex justify-between items-center mt-4">

  {/* Rows per page */}
  <div className="text-sm text-gray-600 flex items-center gap-2">
    <span>Rows:</span>
    <div className="border rounded overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
      <select
        value={itemsPerPage}
        onChange={(e) => {
          setItemsPerPage(Number(e.target.value));
          setPage(1);
        }}
        className="px-2 py-1 outline-none bg-white"
      >
        {[5, 10, 15].map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    </div>
  </div>

  {/* Pagination */}
  <div className="flex items-center gap-1">

    {/* First Page */}
    <button
      onClick={() => setPage(1)}
      disabled={page === 1}
      className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      <ChevronsLeft size={18} />
    </button>

    {/* Prev Page */}
    <button
      onClick={() => setPage((p) => Math.max(p - 1, 1))}
      disabled={page === 1}
      className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      <ChevronLeft size={18} />
    </button>

    {/* Page Numbers */}
    {Array.from({ length: totalPages }, (_, i) => i + 1)
      .slice(
        Math.max(0, page - 2),
        Math.min(totalPages, page + 1)
      )
      .map((n) => (
        <button
          key={n}
          onClick={() => setPage(n)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            page === n
  ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]"
  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          {n}
        </button>
      ))}

    {/* Next Page */}
    <button
      onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
      disabled={page === totalPages}
      className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      <ChevronRight size={18} />
    </button>

    {/* Last Page */}
    <button
      onClick={() => setPage(totalPages)}
      disabled={page === totalPages}
      className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      <ChevronsRight size={18} />
    </button>

  </div>
</div>
      </div>
      

      {/* ✅ Modals */}
      {showModal && (
        <Modal title="Add Designation" onClose={() => setShowModal(false)}>
          <DesignationForm
            departments={departments}
            onSubmit={handleAdd}
            onCancel={() => setShowModal(false)}
          />
        </Modal>
      )}

      {editModal && (
        <Modal title="Edit Designation" onClose={() => setEditModal(false)}>
          <DesignationForm
            designation={selectedDesignation}
            departments={departments}
            onSubmit={handleEdit}
            onCancel={() => setEditModal(false)}
          />
        </Modal>
      )}

      {deleteModal && (
        <Modal title="Confirm Deletion" onClose={() => setDeleteModal(false)}>
          <p className="text-gray-700 mb-4">
            Delete <b>{selectedDesignation?.name}</b>?
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeleteModal(false)}
              className="px-4 py-2 border rounded-lg text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm"
            >
              Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* 🔹 Modal & Form */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
        >
          ✕
        </button>
        <h3 className="text-lg font-semibold mb-4 text-gray-800">{title}</h3>
        {children}
      </div>
    </div>
  );
}

/* 🔸 Form with department dropdown */
function DesignationForm({ designation = {}, departments = [], onSubmit, onCancel }) {
  const [formData, setFormData] = useState({
    name: designation.name || "",
    department_id: designation.department_id || "",
    description: designation.description || "",
    status: designation.status || "active",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div>
        <label className="text-sm text-gray-700">Designation Name</label>
        <div className="mt-1 border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 text-sm outline-none"
            required
          />
        </div>
      </div>

      <div>
        <label className="text-sm text-gray-700">Description</label>
        <div className="mt-1 border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
          <textarea
            rows="3"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            className="w-full px-3 py-2 text-sm outline-none"
            required
          />
        </div>
      </div>

      <div>
        <label className="text-sm text-gray-700">Department</label>
        <div className="mt-1 border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
          <select
            value={formData.department_id}
            onChange={(e) =>
              setFormData({ ...formData, department_id: e.target.value })
            }
            className="w-full px-3 py-2 text-sm outline-none bg-white"
            required
          >
            <option value="">Select Department</option>
            {departments.filter((dept) => dept.status === "active").map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="text-sm text-gray-700">Status</label>
        <div className="mt-1 border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff]/40">
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="w-full px-3 py-2 text-sm outline-none bg-white"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border rounded-lg text-sm"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2  text-white rounded-lg text-sm"
       style={{ 
  background: "linear-gradient(135deg, #2563eb, #d12465)"
}} >
          Save
        </button>
      </div>
    </form>
  );
}